import React from 'react';
import { object, bool, string, number, oneOf, oneOfType } from 'prop-types';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';

import { SUBSCRIPTION } from 'constants/checkoutCollectionGroupTypes';
import { PREPACK } from 'constants/checkoutCollectionTypes';
import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';

import Link from 'components/Link';
import CountdownDate from 'components/CountdownDate';

import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import { CONTENT_PREPACK_TEMPLATE } from 'constants/routingPath';

// eslint-disable-next-line css-modules/no-unused-class
import checkoutCollectionStyles from 'styles/checkoutCollection.scss';
import Price from '../Price';
import PaymentOptionButton from '../PaymentOptionButton';
import paymentOptionStyles from '../PaymentOption/PaymentOption.scss';
import styles from './PaymentPrepackOption.scss';

export default function PaymentPrepackOption({
  id,
  bannerImageUrl,
  displayPrice,
  hasPresale,
  plan,
  presaleExpiredAt,
  contentType,
  contentId,
  groupType,
}) {
  useStyles(styles, paymentOptionStyles, checkoutCollectionStyles);

  const { t } = useTranslation(['purchase']);

  const getOfficialSiteURL = useGetOfficialSiteURL();

  const { name, highlight, description, planType } = plan;
  const { originalPrice, sellingPrice, discount } = displayPrice;

  const showPromotionSection = highlight || hasPresale;

  const renderPromotionLabel = () => {
    if (highlight)
      return (
        <div className={checkoutCollectionStyles.labelAccent}>{highlight}</div>
      );
    if (hasPresale)
      return (
        <div className={checkoutCollectionStyles.label}>
          {t('purchase.promoOfferTag.limited')}
        </div>
      );

    return null;
  };

  return (
    <div className={paymentOptionStyles.root}>
      <div className={styles.details}>
        <div className={styles.banner}>
          {bannerImageUrl && (
            <img
              src={bannerImageUrl}
              alt={name}
              className={styles.bannerImage}
            />
          )}
        </div>

        <div className={styles.information}>
          <h2 className={styles.name}>{name}</h2>
          <p className={styles.description}>{description}</p>
          <Link
            className={styles.link}
            to={getOfficialSiteURL(
              `${CONTENT_PREPACK_TEMPLATE}#${contentType}=${contentId}`,
              {
                id,
              },
            )}
          >
            {t('purchase.content.prePackedPlanMoreCta')}
          </Link>
        </div>
      </div>
      {showPromotionSection && (
        <div className={checkoutCollectionStyles.promotion}>
          {renderPromotionLabel()}
          {hasPresale && <CountdownDate dateTo={presaleExpiredAt} />}
        </div>
      )}
      <div className={paymentOptionStyles.planContent}>
        <Price
          className={paymentOptionStyles.price}
          perMonth={plan.billingCyclePerMonths}
          sellingPrice={sellingPrice}
          originalPrice={originalPrice}
          hasDiscount={!!discount}
          hasUnit={groupType === SUBSCRIPTION || planType === SUBSCRIPTION}
        />
      </div>

      <PaymentOptionButton
        type={PREPACK}
        checkoutId={id}
        discount={discount}
        isHighlighted={!!highlight}
        contentType={contentType}
        contentId={contentId}
      />
    </div>
  );
}

PaymentPrepackOption.propTypes = {
  id: number.isRequired,
  hasPresale: bool.isRequired,
  plan: object.isRequired,
  contentType: oneOf([COURSE, REGULAR_CLASS]).isRequired,
  contentId: oneOfType([string, number]).isRequired,
  groupType: string.isRequired,
  bannerImageUrl: string,
  description: string,
  displayPrice: object,
  presaleExpiredAt: string,
};

PaymentPrepackOption.defaultProps = {
  bannerImageUrl: undefined,
  description: undefined,
  displayPrice: undefined,
  presaleExpiredAt: undefined,
};
